import { computed, ref, unref, type Ref } from 'vue'
import { useMutation, useQueries, useQuery } from '@tanstack/vue-query'
import useApi from '@/composables/api'
import { QUERY_KEYS } from '@/constants/query'
import type {
    PeopleActionSearchSort,
    PeopleDetailPersonDetailsResource,
    PersonSummaryResource
} from '@/services/api/client'
import { map, join, uniq, flatMap } from 'lodash'
import queryClient from '@/plugins/query'
import { plainToInstance } from 'class-transformer'
import PaginationMeta from '@/services/api/transformers/PaginationMeta'
import PersonSearchResponse from '@/services/api/transformers/PersonSearchResponse'
import PersonSearchResource from '@/services/api/transformers/PersonSearchResource'
import PersonDetailResource from '@/services/api/transformers/PersonDetailResource'

const { api, call } = useApi()
const usePeople = (filters: Ref<string>, page: Ref<number>) => {
    const { data, status } = useQuery({
        queryKey: QUERY_KEYS.peopleSearch(filters, page),
        queryFn: async () => {
            const pageParam = page.value || 1
            const { data, pagination } = await call(`GET`, `/people/search?${filters.value}&page=${pageParam}`)

            return {
                data: map(data, person =>
                    plainToInstance(PersonSearchResponse, person)
                ) as unknown as PersonSearchResponse[],
                pagination: plainToInstance(PaginationMeta, pagination) as unknown as PaginationMeta
            }
        },
        enabled: computed(() => Boolean(filters.value?.length))
    })

    return { data, status }
}

const usePeopleByIds = (ids: Ref<number[]>, enabled?: Ref, include?: Ref) => {
    const { api } = useApi()

    // Filter out invalid IDs before creating queries
    const validIds = computed(() => ids.value.filter(id => id !== null && id !== 0 && id !== undefined))

    const queries = useQueries({
        queries: computed(() =>
            validIds.value.map(id => ({
                queryKey: QUERY_KEYS.personsById.detail(ref(id)),
                queryFn: () =>
                    api.people
                        .getOnePerson({
                            id,
                            include: include?.value
                        })
                        .then(({ data }) => data as PersonDetailResource[]),
                select: (data: unknown) => plainToInstance(PersonDetailResource, data as PersonDetailResource[]),
                enabled: computed(() => (enabled ? enabled.value ?? true : true)),
                onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.personsById.all })
            }))
        )
    })

    const people = computed<PersonDetailResource[]>(() => {
        const allIds = flatMap(queries.value, query =>
            query.isSuccess && query.data ? (query.data as PersonDetailResource[]) : []
        )
        return uniq(allIds)
    })

    const isLoading = computed(() => queries.value.some(query => query.isFetching))

    return {
        data: people,
        isLoading
    }
}

const usePeopleFilterByName = (name: Ref<string>) => {
    const queryObject = {
        'filter[is_archived:eq:boolean]': 'false',
        'filter[include_family:eq:boolean]': 'true',
        'sort': '[formalName:asc]',
        'personNameFormat': 'natural',
        'limit': '8'
    }
    const queryString = new URLSearchParams(queryObject).toString()

    const { data, status, isLoading } = useQuery({
        queryKey: QUERY_KEYS.peopleNameSearch(name),
        queryFn: () => call('GET', `/people?${queryString}&filter[name:name_filter]=${name.value}`),
        select: ({ data }) =>
            map(data, person => plainToInstance(PersonSearchResource, person)) as PersonSearchResource[],
        enabled: computed(() => Boolean(name.value?.length))
    })

    return { data, status, isLoading }
}

const useGetFamilyMembers = (familyIds: Ref<number[]> = ref([])) =>
    useQuery({
        queryKey: QUERY_KEYS.peopleFamilyMembers(familyIds),
        queryFn: () =>
            call(
                'GET',
                `/people?collection_filter[id:contains:array]=${join(
                    familyIds.value,
                    ','
                )}&include=profile_fields,family`
            ),
        select: ({ data }) =>
            map(data, (person: PeopleDetailPersonDetailsResource) => plainToInstance(PersonDetailResource, person)),
        enabled: computed(() => Boolean(familyIds.value.length))
    })

const usePeopleUnarchive = () => {
    return useMutation({
        mutationFn: ({
            checkedPeople,
            uncheckedPeople,
            allSelected,
            filters,
            sort
        }: {
            checkedPeople?: Ref<number[]>
            uncheckedPeople?: Ref<number[]>
            allSelected: Ref<boolean>
            filters: Record<string, string>
            sort?: PeopleActionSearchSort[]
        }) =>
            api.peopleArchive.unarchivePeople({
                requestBody: {
                    filters: { filters, sort },
                    ...(checkedPeople?.value ? { checkedPeople: checkedPeople?.value } : []),
                    ...(uncheckedPeople?.value ? { uncheckedPeople: uncheckedPeople?.value } : []),
                    allSelected: allSelected.value
                }
            }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.people })
    })
}

const usePeopleArchive = () => {
    return useMutation({
        mutationFn: ({
            checkedPeople,
            uncheckedPeople,
            allSelected,
            filters,
            sort
        }: {
            checkedPeople?: Ref<number[]>
            uncheckedPeople?: Ref<number[]>
            allSelected: Ref<boolean>
            filters: Record<string, string>
            sort?: PeopleActionSearchSort[]
        }) =>
            api.peopleArchive.archivePersonCreate({
                requestBody: {
                    filters: { filters, sort },
                    ...(checkedPeople?.value ? { checkedPeople: checkedPeople?.value } : []),
                    ...(uncheckedPeople?.value ? { uncheckedPeople: uncheckedPeople?.value } : []),
                    allSelected: allSelected.value
                }
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.people })
        }
    })
}

const usePeopleDuplicateCheck = (firstName: Ref<string>, lastName: Ref<string>) => {
    const { data } = useQuery({
        queryKey: QUERY_KEYS.peopleDuplicateCheck(firstName, lastName),
        queryFn: async () => {
            const params = new URLSearchParams({
                'filter[name.first:contains]': firstName.value.trim(),
                'filter[name.last:contains]': lastName.value.trim(),
                'filter[archived:exists]': 'false',
                'sort': '[last_name:asc,first_name:asc]',
                'page': '1'
            }).toString()

            const { data } = await call('GET', `/people/search?${params}`)
            return map(data, person =>
                plainToInstance(PersonSearchResponse, person)
            ) as unknown as PersonSearchResponse[]
        },
        enabled: computed(() => firstName.value.trim().length > 0 && lastName.value.trim().length > 0)
    })

    return { data }
}

/** The slice of a Breeze person the Inbox Lite panel needs to link and picture a row. */
interface PersonByMdsId {
    id: number
    /** Breeze profile picture path (relative to the file host) or `null`. */
    profilePicture: string | null
}

/**
 * Resolves Tithely member ids (`mds_id`) to Breeze people in one request, via
 * `GET /people/by-mds-id`. Used for cross-app hops out of the Inbox Lite panel into
 * `/people/view/{id}/message`. Ids the caller can't see are absent from the result,
 * so callers should treat a missing key as "no link", not an error.
 */
const usePeopleByMdsIds = (mdsIds: Ref<string[]>, enabled: Ref<boolean> | boolean = true) =>
    useQuery({
        queryKey: QUERY_KEYS.peopleByMdsIds(mdsIds),
        // Built by hand rather than via `api.people.getPeopleByMdsIds`: the generated
        // client serializes arrays as repeated `mds_ids=a&mds_ids=b`, and PHP keeps only
        // the last value, so Laravel rejects it with "The mds ids must be an array."
        // Laravel needs the `mds_ids[]=` form.
        queryFn: (): Promise<{ data: PersonSummaryResource[] }> => {
            const params = new URLSearchParams()
            mdsIds.value.forEach(id => params.append(`mds_ids[]`, id))

            return call(`GET`, `/people/by-mds-id?${params}`)
        },
        select: ({ data }): Record<string, PersonByMdsId> =>
            Object.fromEntries(
                data.flatMap(person =>
                    person.mds_id && person.id
                        ? [[person.mds_id, { id: person.id, profilePicture: person.profile_picture ?? null }]]
                        : []
                )
            ),
        enabled: computed(() => unref(enabled) && mdsIds.value.length > 0)
    })

export type { PersonByMdsId }
export {
    usePeopleByMdsIds,
    usePeople,
    usePeopleUnarchive,
    usePeopleArchive,
    usePeopleByIds,
    usePeopleFilterByName,
    useGetFamilyMembers,
    usePeopleDuplicateCheck
}
